<template>
  <div class="reasoning-block my-2" style="max-width: 85%;">
    <!-- Collapsible header -->
    <div
      class="reasoning-header d-flex align-center ga-2 py-2 px-1"
      @click="expanded = !expanded"
      style="cursor: pointer;"
    >
      <v-icon :icon="expanded ? 'mdi-chevron-down' : 'mdi-chevron-right'" size="small" color="medium-emphasis" />
      <v-icon icon="mdi-brain" size="small" color="medium-emphasis" />
      <span :class="['text-body-2', streaming ? 'shimmer-text' : 'text-medium-emphasis']">{{ summaryText }}</span>
    </div>

    <!-- Reasoning text. Plain text with preserved whitespace — reasoning is
         freeform and may contain partial markdown, so render it muted and
         informational, not as an executed action. -->
    <div v-if="expanded" class="reasoning-body ml-3 text-medium-emphasis">{{ text }}</div>
  </div>
</template>

<script setup>
import { useI18n } from '../../composables/useI18n.js'
const { t } = useI18n()
import { ref, computed, watch } from 'vue'

const props = defineProps({
  block: { type: Object, required: true },
})

const streaming = computed(() => props.block.items.some(i => i._streaming))

// Open while the model is actively reasoning, then tuck away once the answer
// (or a tool call) begins. Start collapsed for already-finished blocks
// (history / resume). A manual collapse mid-stream is respected because the
// watcher only fires on streaming-state transitions.
const expanded = ref(streaming.value)
watch(streaming, (isStreaming) => { expanded.value = isStreaming })

const text = computed(() =>
  props.block.items.map(i => i.content || '').join('\n').trim()
)

const summaryText = computed(() => (streaming.value ? t('chat.thinking') : t('chat.thoughtProcess')))
</script>

<style scoped>
.reasoning-header:hover {
  opacity: 0.8;
}
.reasoning-body {
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 0.8125rem;
  line-height: 1.5;
  padding-bottom: 4px;
}
/* Text shimmer animation — light sweep across the text while reasoning streams */
.shimmer-text {
  background: linear-gradient(
    90deg,
    rgba(var(--v-theme-on-surface), 0.4) 0%,
    rgba(var(--v-theme-on-surface), 0.4) 40%,
    rgba(var(--v-theme-on-surface), 0.9) 50%,
    rgba(var(--v-theme-on-surface), 0.4) 60%,
    rgba(var(--v-theme-on-surface), 0.4) 100%
  );
  background-size: 200% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: text-shimmer 1.4s ease-in-out infinite;
}
@keyframes text-shimmer {
  0%   { background-position: 100% 50%; }
  100% { background-position: -100% 50%; }
}
@media (prefers-reduced-motion: reduce) {
  .shimmer-text { animation: none; }
}
</style>
